type FontCatalogItem = {
  id?: string | number;
  title?: string;
  type?: string;
  filename?: unknown;
  path?: string;
  url?: string;
  published?: unknown;
  font_preview_url?: string;
};

type FontCatalog = {
  fonts: FontCatalogItem[];
  googleSpecs: Map<string, string>;
};

const SYSTEM_FONT_KEYS = new Set([
  'arial',
  'helvetica',
  'times+new+roman',
  'courier+new',
  'georgia',
  'verdana',
  'tahoma',
  'trebuchet+ms',
  'impact',
]);

let catalogPromise: Promise<FontCatalog> | null = null;
const googleFontLoads = new Map<string, Promise<void>>();
const injectedCustomFonts = new Set<string>();

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function cssEscapeSingleQuoted(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

function cssEscapeDoubleQuoted(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

export function normalizeDefaultTextLayerFontTitle(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, '+');
}

export function parseDefaultTextLayerGoogleSpecs(raw: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const spec of raw.split('|').map((s) => s.trim()).filter(Boolean)) {
    const key = normalizeDefaultTextLayerFontTitle(spec.split(':')[0] ?? '');
    if (key) out.set(key, spec);
  }
  return out;
}

export function parseDefaultTextLayerFontFiles(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map(String).filter(Boolean);
  }
  if (isRecord(raw)) {
    return Object.values(raw).map(String).filter(Boolean);
  }
  if (typeof raw !== 'string' || raw.trim() === '') {
    return [];
  }
  try {
    return parseDefaultTextLayerFontFiles(JSON.parse(raw));
  } catch {
    return [raw.trim()];
  }
}

export function defaultTextLayerFontFormat(file: string): string {
  const ext = file.split('?')[0]?.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'woff2') return 'woff2';
  if (ext === 'woff') return 'woff';
  if (ext === 'otf') return 'opentype';
  if (ext === 'ttf') return 'truetype';
  return '';
}

export function resolveDefaultTextLayerFontUrl(font: FontCatalogItem, baseUrl: string): string {
  const files = parseDefaultTextLayerFontFiles(font.filename);
  const file = files.find((f) => /\.(woff2?|ttf|otf)(\?.*)?$/i.test(f));
  if (!file) return '';
  if (/^https?:\/\//i.test(file) || file.startsWith('/')) return file;
  if (font.font_preview_url) return font.font_preview_url;

  const path = String(font.path ?? '').replace(/\\/g, '/').replace(/^\/+|\/+$/g, '');
  const rawBase = String(font.url || `${baseUrl.replace(/\/$/, '')}/${path}`);
  return `${rawBase.replace(/\/$/, '')}/${file.replace(/^\/+/, '')}`;
}

async function fetchFontCatalog(baseUrl: string): Promise<FontCatalog> {
  if (!baseUrl || typeof fetch !== 'function') {
    return { fonts: [], googleSpecs: new Map() };
  }
  const response = await fetch(`${baseUrl.replace(/\/$/, '')}/ajax.php?type=fonts`, {
    method: 'POST',
    credentials: 'same-origin',
  });
  const data = await response.json().catch(() => ({}));
  if (!isRecord(data) || data.status !== 1 || !isRecord(data.fonts)) {
    return { fonts: [], googleSpecs: new Map() };
  }
  const fonts = Array.isArray(data.fonts.fonts) ? data.fonts.fonts.filter(isRecord) : [];
  const googleSpecs = parseDefaultTextLayerGoogleSpecs(
    typeof data.fonts.google_fonts === 'string' ? data.fonts.google_fonts : '',
  );
  return { fonts, googleSpecs };
}

async function loadFontCatalog(baseUrl: string): Promise<FontCatalog> {
  if (!catalogPromise) {
    catalogPromise = fetchFontCatalog(baseUrl).catch(() => ({ fonts: [], googleSpecs: new Map() }));
  }
  return catalogPromise;
}

function injectGoogleFont(family: string, googleSpecs: Map<string, string>): Promise<void> {
  const key = normalizeDefaultTextLayerFontTitle(family);
  if (!key) return Promise.resolve();
  const existing = googleFontLoads.get(key);
  if (existing) return existing;

  const spec = googleSpecs.get(key) ?? family.trim().replace(/\s+/g, '+');
  const load = new Promise<void>((resolve) => {
    const link = document.createElement('link');
    const done = () => resolve();
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css?family=${encodeURI(spec)}&display=swap`;
    link.onload = done;
    link.onerror = done;
    window.setTimeout(done, 2000);
    document.head.appendChild(link);
  });
  googleFontLoads.set(key, load);
  return load;
}

function injectCustomFont(font: FontCatalogItem, baseUrl: string): void {
  const title = String(font.title ?? '').trim();
  const key = normalizeDefaultTextLayerFontTitle(title);
  if (!key || injectedCustomFonts.has(key)) return;
  const url = resolveDefaultTextLayerFontUrl(font, baseUrl);
  if (!url) return;
  const format = defaultTextLayerFontFormat(url);
  const cssTitle = cssEscapeSingleQuoted(title);
  const style = document.createElement('style');
  style.textContent = `@font-face{font-family:'${cssTitle}';font-style:normal;font-weight:400;src:local('${cssTitle}'),url(${JSON.stringify(url)})${format ? ` format('${format}')` : ''};}`;
  document.head.appendChild(style);
  injectedCustomFonts.add(key);
}

async function waitForFontFace(family: string): Promise<void> {
  const fonts = document.fonts;
  if (!fonts?.load) return;
  const quoted = `"${cssEscapeDoubleQuoted(family)}"`;
  await Promise.race([
    fonts.load(`16px ${quoted}`).then(() => undefined),
    new Promise<void>((resolve) => window.setTimeout(resolve, 1200)),
  ]);
}

export async function ensureDefaultTextLayerFontLoaded(family: string, baseUrl: string): Promise<void> {
  const fontFamily = family.trim();
  const key = normalizeDefaultTextLayerFontTitle(fontFamily);
  if (!fontFamily || SYSTEM_FONT_KEYS.has(key)) return;

  const catalog = await loadFontCatalog(baseUrl);
  const font = catalog.fonts.find((f) => normalizeDefaultTextLayerFontTitle(String(f.title ?? '')) === key);
  if (!font) return;
  if (String(font.published ?? '1') === '0') return;

  const type = String(font.type ?? '').toLowerCase().trim();
  if (type === 'google' || type === '') {
    await injectGoogleFont(fontFamily, catalog.googleSpecs);
  } else {
    injectCustomFont(font, baseUrl);
  }

  await waitForFontFace(fontFamily).catch(() => undefined);
}
